import { useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { createRecipe, importRecipe } from '../../api/recipes.js';
import { useToast } from '../../hooks/useToast.js';
import type {
  ImportResult,
  ImportedIngredientDraft,
  MealType,
} from '../../api/types.js';
import { IMPORTED_UNITS, MEAL_TYPES } from '../../api/types.js';

interface ScraperDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

type Stage = 'url' | 'verify';

interface EditableIngredient {
  id: number | null;
  name: string;
  amount: number;
  unit: string;
  needs_review: boolean;
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-primary, transparent)',
  borderColor: 'var(--border)',
  color: 'var(--text-main)',
};

const buttonStyle: React.CSSProperties = {
  borderColor: 'var(--border)',
  color: 'var(--text-main)',
  minHeight: 40,
};

function draftToEditable(d: ImportResult): {
  title: string;
  meal_type: MealType;
  default_portions: number;
  prep_time_minutes: number;
  instructions: string;
  ingredients: EditableIngredient[];
  warnings: string[];
} {
  return {
    title: d.title,
    meal_type: 'dinner',
    default_portions: d.default_portions,
    prep_time_minutes: d.prep_time_minutes,
    instructions: d.instructions,
    ingredients: d.ingredients.map((i: ImportedIngredientDraft) => ({
      id: i.id,
      name: i.name,
      amount: i.amount,
      unit: i.unit,
      needs_review: i.needs_review,
    })),
    warnings: d.warnings,
  };
}

export function ScraperDialog({
  open,
  onClose,
  onSaved,
}: ScraperDialogProps): ReactElement | null {
  const toast = useToast();
  const [stage, setStage] = useState<Stage>('url');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stage 2 form state
  const [title, setTitle] = useState('');
  const [mealType, setMealType] = useState<MealType>('dinner');
  const [defaultPortions, setDefaultPortions] = useState<number>(2);
  const [prepTime, setPrepTime] = useState<number>(15);
  const [instructions, setInstructions] = useState('');
  const [ingredients, setIngredients] = useState<EditableIngredient[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [sourceUrl, setSourceUrl] = useState('');

  if (!open) return null;

  const busy = loading || saving;
  // While busy, swallow close (Dialog calls onClose on Esc + backdrop click).
  const handleClose = busy ? () => undefined : onClose;

  function reset(): void {
    setStage('url');
    setUrl('');
    setError(null);
    setTitle('');
    setMealType('dinner');
    setDefaultPortions(2);
    setPrepTime(15);
    setInstructions('');
    setIngredients([]);
    setWarnings([]);
    setSourceUrl('');
  }

  async function onFetch(): Promise<void> {
    const trimmed = url.trim();
    if (!/^https?:\/\//.test(trimmed)) {
      setError('URL must start with http:// or https://');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await importRecipe(trimmed);
      const draft = draftToEditable(result);
      setTitle(draft.title);
      setMealType(draft.meal_type);
      setDefaultPortions(draft.default_portions);
      setPrepTime(draft.prep_time_minutes);
      setInstructions(draft.instructions);
      setIngredients(draft.ingredients);
      setWarnings(draft.warnings);
      setSourceUrl(result.source_url);
      setStage('verify');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setError(msg);
      toast.push('error', msg);
    } finally {
      setLoading(false);
    }
  }

  function updateIngredient(idx: number, patch: Partial<EditableIngredient>): void {
    setIngredients((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeIngredient(idx: number): void {
    setIngredients((rows) => rows.filter((_, i) => i !== idx));
  }

  function addIngredient(): void {
    setIngredients((rows) => [
      ...rows,
      { id: null, name: '', amount: 0, unit: 'pcs', needs_review: false },
    ]);
  }

  async function onSave(): Promise<void> {
    setSaving(true);
    try {
      const payloadIngredients = ingredients
        .filter((r) => r.id !== null && r.name.trim().length > 0)
        .map((r) => ({
          ingredient_id: r.id as number,
          amount: Number(r.amount),
          unit: r.unit as 'g' | 'ml' | 'tbsp' | 'tsp' | 'pcs',
        }));
      if (payloadIngredients.length === 0) {
        toast.push('error', 'Add at least one ingredient');
        setSaving(false);
        return;
      }
      await createRecipe({
        title: title.trim().length > 0 ? title.trim() : 'Untitled Recipe',
        meal_type: mealType,
        portions: Math.max(1, Math.floor(defaultPortions)),
        instructions: instructions,
        prep_time_minutes: Math.max(1, Math.floor(prepTime)),
        source_url: sourceUrl,
        ingredients: payloadIngredients,
      });
      toast.push('success', 'Recipe saved');
      onSaved();
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.push('error', msg);
    } finally {
      setSaving(false);
    }
  }

  function onDiscard(): void {
    reset();
    onClose();
  }

  // Stage 1: URL input
  if (stage === 'url') {
    return (
      <Dialog open={open} onClose={handleClose} title="🌐 Import Recipe from URL">
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Recipe URL</span>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/my-recipe"
              disabled={loading}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
              data-testid="scraper-url"
            />
          </label>
          {error !== null ? (
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: '#dc2626',
                background: 'color-mix(in srgb, #dc2626 12%, transparent)',
                color: '#dc2626',
              }}
              data-testid="scraper-error"
            >
              {error}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onDiscard}
              disabled={loading}
              className="rounded-md border px-3 py-2 text-sm"
              style={buttonStyle}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onFetch()}
              disabled={loading}
              className="rounded-md border px-3 py-2 text-sm font-medium"
              style={{
                background: 'var(--accent)',
                borderColor: 'var(--accent)',
                color: 'var(--bg-primary, #fff)',
                minHeight: 40,
                opacity: loading ? 0.6 : 1,
              }}
              data-testid="scraper-submit"
            >
              {loading ? 'Fetching…' : 'Fetch Recipe'}
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  // Stage 2: Verify & Edit
  const unrecognizedNames = new Set(
    ingredients.filter((i) => i.needs_review).map((i) => i.name),
  );
  return (
    <Dialog open={open} onClose={handleClose} title="🌐 Verify Imported Recipe">
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
        {warnings.length > 0 ? (
          <div
            className="rounded-md border p-3 text-sm"
            style={{
              borderColor: '#f59e0b',
              background: 'color-mix(in srgb, #f59e0b 12%, transparent)',
              color: 'var(--text-main)',
            }}
            data-testid="scraper-warnings"
          >
            <div className="mb-1 font-medium" style={{ color: '#b45309' }}>
              ⚠ Imported with warnings
            </div>
            <ul className="list-disc pl-5">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
            <div className="mt-1 text-xs" style={{ color: '#b45309' }}>
              New ingredients were added with category &apos;Other&apos; and estimated default
              macros. Please verify their nutritional values.
            </div>
          </div>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
            data-testid="scraper-title"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Meal type</span>
          <select
            value={mealType}
            onChange={(e) => setMealType(e.target.value as MealType)}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
            data-testid="scraper-meal-type"
          >
            {MEAL_TYPES.map((m) => (
              <option key={m} value={m}>
                {m[0]!.toUpperCase() + m.slice(1)}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Default portions</span>
            <input
              type="number"
              min={1}
              value={defaultPortions}
              onChange={(e) => setDefaultPortions(Number(e.target.value))}
              disabled={saving}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
              data-testid="scraper-portions"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Prep time (min)</span>
            <input
              type="number"
              min={1}
              value={prepTime}
              onChange={(e) => setPrepTime(Number(e.target.value))}
              disabled={saving}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
              data-testid="scraper-prep-time"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Instructions</span>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            disabled={saving}
            rows={5}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
            data-testid="scraper-instructions"
          />
        </label>

        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium" style={{ color: 'var(--text-main)' }}>
            Ingredients
          </div>
          {ingredients.length === 0 ? (
            <div
              className="rounded-md border p-2 text-sm"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              data-testid="scraper-no-ingredients"
            >
              No ingredients were found on the page
            </div>
          ) : null}
          {ingredients.map((row, idx) => {
            const review = row.needs_review || unrecognizedNames.has(row.name);
            return (
              <div
                key={idx}
                className="grid grid-cols-[1fr_80px_90px_36px] items-center gap-1"
                data-testid={`scraper-ingredient-${idx}`}
              >
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => updateIngredient(idx, { name: e.target.value })}
                  disabled={saving}
                  className="rounded-md border px-2 py-1 text-sm"
                  style={inputStyle}
                  placeholder="name"
                  data-testid={`scraper-ingredient-name-${idx}`}
                />
                <input
                  type="number"
                  min={0}
                  value={row.amount}
                  onChange={(e) => updateIngredient(idx, { amount: Number(e.target.value) })}
                  disabled={saving}
                  className="rounded-md border px-2 py-1 text-sm"
                  style={inputStyle}
                  placeholder="amount"
                  data-testid={`scraper-ingredient-amount-${idx}`}
                />
                <select
                  value={row.unit}
                  onChange={(e) => updateIngredient(idx, { unit: e.target.value })}
                  disabled={saving}
                  className="rounded-md border px-2 py-1 text-sm"
                  style={inputStyle}
                  data-testid={`scraper-ingredient-unit-${idx}`}
                >
                  {IMPORTED_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeIngredient(idx)}
                  disabled={saving}
                  className="rounded-md border px-1 py-1 text-sm"
                  style={buttonStyle}
                  aria-label="Remove ingredient"
                  data-testid={`scraper-ingredient-remove-${idx}`}
                >
                  🗑️
                </button>
                {review ? (
                  <div
                    className="col-span-4 text-xs"
                    style={{ color: '#b45309' }}
                    data-testid={`scraper-needs-review-${idx}`}
                  >
                    ⚠ needs review
                  </div>
                ) : null}
              </div>
            );
          })}
          <button
            type="button"
            onClick={addIngredient}
            disabled={saving}
            className="self-start rounded-md border px-3 py-1 text-sm"
            style={buttonStyle}
            data-testid="scraper-ingredient-add"
          >
            + Add ingredient
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm"
            style={buttonStyle}
            data-testid="scraper-discard"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={saving}
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              borderColor: 'var(--accent)',
              color: 'var(--bg-primary, #fff)',
              minHeight: 40,
              opacity: saving ? 0.6 : 1,
            }}
            data-testid="scraper-save"
          >
            {saving ? 'Saving…' : 'Save Recipe'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
